import { ENDPOINTS } from "@/common/service/config/endpoint";
import { axiosPublicBaseQuery, queryHandler } from "@/common/service/request";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AcceptPaymentPayloadType,
  AcceptPaymentNoReferenceResponseType,
  PaylinkRecipientType,
  PaymentStatusResponseType,
  VerifyKycResponseType,
  VerifyKycWithEmailParams,
  CurrencySwap,
  IcurrencySwap,
  IAssetType,
  ExchangeRate,
  IexchangeRate,
  IPaymentLinkType,
} from "./type";
import { APIResponseModel } from "@/common/types";

const {
  ACCEPT_PAYMENT,
  VERIFY_KYC_WITH_EMAIL,
  GET_PAYEE_ACCOUNT_INFO,
  RETRIVE_RECIPIENT,
  COUNTRIES_LIST,
  CURRENCY_SWAP,
  EXCHANGE_RATE,
  GET_SINGLE_PAYMENT_LINK,
} = ENDPOINTS.collections;

export const collectionApi = createApi({
  reducerPath: "collection",
  baseQuery: axiosPublicBaseQuery({ baseUrl: "" }),
  tagTypes: [],
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    retriveRecipient: builder.query<
      APIResponseModel<PaylinkRecipientType>,
      string
    >({
      query: (recipientReference) => {
        const { url: makeUrl, method } = RETRIVE_RECIPIENT;
        const url = makeUrl(recipientReference);
        return {
          url,
          method,
        };
      },
    }),
    getPaymentStatus: builder.mutation<
      APIResponseModel<PaymentStatusResponseType>,
      { appid: string; payer_email: string; txn_hash?: string }
    >({
      query: (data) => {
        const { url: makeUrl, method } = GET_PAYEE_ACCOUNT_INFO;
        const query = queryHandler({ txn_hash: data.txn_hash });
        const url = makeUrl(data.appid, query);
        return {
          url,
          method,
          headers: {
            "x-user-email": data.payer_email,
          },
        };
      },
    }),
    verifyKycWithEmail: builder.query<
      APIResponseModel<VerifyKycResponseType>,
      VerifyKycWithEmailParams
    >({
      query: (params) => {
        const { url: makeUrl, method } = VERIFY_KYC_WITH_EMAIL;
        const query = queryHandler(params);
        const url = makeUrl + query;
        return {
          url,
          method,
        };
      },
    }),
    acceptPayment: builder.mutation<
      APIResponseModel<AcceptPaymentNoReferenceResponseType>,
      AcceptPaymentPayloadType
    >({
      query: (data) => {
        const { url, method } = ACCEPT_PAYMENT;
        return {
          url,
          method,
          data,
          headers: {
            "x-user-email": data.payer_email,
          },
        };
      },
    }),

    currencySwap: builder.mutation<
      APIResponseModel<CurrencySwap>,
      IcurrencySwap
    >({
      query: (data) => {
        const { url, method } = CURRENCY_SWAP;

        return {
          url,
          method,
          data,
        };
      },
    }),
    getSinglePaymentLink: builder.query<
      APIResponseModel<IPaymentLinkType>,
      string
    >({
      query: (id) => {
        const { method, url } = GET_SINGLE_PAYMENT_LINK;

        return { url: url(id), method };
      },
    }),

    exchangeRate: builder.query<APIResponseModel<ExchangeRate>, IexchangeRate>({
      query: (data) => {
        const { url, method } = EXCHANGE_RATE;
        const { app_id, ...rest } = data;
        const query = queryHandler(rest);

        return {
          url: url(app_id, query),
          method,
        };
      },
    }),

    getAssets: builder.query<APIResponseModel<{ data: IAssetType[] }>, void>({
      query: () => {
        const { url: makeUrl, method } = COUNTRIES_LIST;
        const url = makeUrl("global_transfer");
        return {
          url,
          method,
        };
      },
    }),
  }),
});

export const {
  useAcceptPaymentMutation,
  useVerifyKycWithEmailQuery,
  useLazyVerifyKycWithEmailQuery,
  useRetriveRecipientQuery,
  useGetPaymentStatusMutation,
  useGetAssetsQuery,
  useCurrencySwapMutation,
  useExchangeRateQuery,
  useGetSinglePaymentLinkQuery,
} = collectionApi;
